import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCard, BarChart2, Users, AlertCircle } from 'lucide-react';
import { useBalance } from '../hooks/useBalance';
import { Button } from './ui';
import axios from 'axios';
import { billingErrorMessage } from '../utils/billingErrors';

function BillingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workflowId = searchParams.get('workflow_id');
  const { balance, refetch } = useBalance(true);

  const [tier, setTier] = useState(null);
  useEffect(() => {
    axios.get('/api/v1/org/billing')
      .then(r => setTier(r.data?.subscription?.tier || null))
      .catch(() => setTier(null));
  }, []);

  const isTeamOrEnt = tier && (tier === 'TEAM' || tier === 'ENTERPRISE');

  const packages = [
    { credits: 1000, gbp: 10 },
    { credits: 2500, gbp: 25 },
    { credits: 5000, gbp: 50 },
  ];
  const isDev = process.env.REACT_APP_DEV_MODE === 'true' || process.env.NODE_ENV === 'development';

  const handleTopUp = async (gbp) => {
    try {
      const response = await axios.post('/api/v1/topup/checkout', {
        amount: gbp,
        workflow_id: workflowId || null
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Failed to initiate checkout:', error?.response?.status, error?.message);
      alert(billingErrorMessage(error, 'Failed to start payment. Please try again.'));
    }
  };

  const handleDevGrant = async () => {
    try {
      await axios.post('/api/v1/topup/dev-grant', { credits: 1000 });
      refetch();
    } catch (error) {
      console.error('Dev grant failed:', error?.response?.status, error?.message);
      alert(billingErrorMessage(error, 'Dev grant failed'));
    }
  };

  return (
    <div className="min-h-dvh bg-app-bg py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl border border-app-border shadow-sm p-8">
          <div className="flex items-center justify-between mb-1">
            <h1 className="text-2xl font-semibold text-app-text">Top up balance</h1>
            <Button variant="ghost" size="sm" onClick={() => navigate('/billing/dashboard')}>
              <BarChart2 className="w-4 h-4" />
              Dashboard
            </Button>
          </div>
          <p className="text-app-text-muted mb-8">
            Add credits to keep your AI workflows running.
          </p>

          {/* Current balance — hidden for TEAM/ENT (lives in org dashboard) */}
          {balance && !isTeamOrEnt && (
            <div className="bg-app-panel-muted border border-app-border rounded-lg p-4 mb-6">
              <div className="text-xs uppercase tracking-wide text-app-text-subtle mb-1">Current balance</div>
              <div className="text-2xl font-semibold text-app-text tabular-nums">
                £{Number(balance.available).toFixed(2)}
              </div>
            </div>
          )}

          {/* Team / Org usage link (TEAM/ENT only) */}
          {isTeamOrEnt && (
            <button
              onClick={() => navigate('/org/billing')}
              className="w-full flex items-center justify-between bg-accent-50 border border-accent-200 hover:bg-accent-100 active:bg-accent-200 rounded-lg px-4 py-3 mb-6 transition-colors focus-ring"
            >
              <div className="flex items-center gap-2 text-sm font-medium text-accent-700">
                <Users className="w-4 h-4" />
                Team usage &amp; balance
              </div>
              <span className="text-xs text-accent-600">View org wallet &amp; per-member spend →</span>
            </button>
          )}

          {/* Workflow context warning */}
          {workflowId && (
            <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
              <div>
                <p className="text-sm text-orange-900">
                  Top up to complete your workflow.
                </p>
                <p className="text-xs text-orange-700 mt-1 font-mono">
                  Workflow ID: {workflowId}
                </p>
              </div>
            </div>
          )}

          {/* Top-up packages */}
          <div className="grid grid-cols-1 gap-3 mb-6">
            {packages.map(({ credits, gbp }) => (
              <button
                key={gbp}
                onClick={() => handleTopUp(gbp)}
                className="bg-white border border-app-border-strong hover:border-accent-500 hover:bg-accent-50/30 active:bg-accent-50/60 rounded-lg p-5 flex items-center justify-between transition-colors duration-150 focus-ring press-tactile text-left"
              >
                <div>
                  <div className="text-xl font-semibold text-app-text tabular-nums">
                    {credits.toLocaleString()} credits
                  </div>
                  <div className="text-sm text-app-text-muted mt-0.5 tabular-nums">
                    {(gbp / credits * 1000).toFixed(1)}p per 1,000 credits
                  </div>
                </div>
                <div className="text-xl font-semibold text-accent-600 tabular-nums">
                  £{gbp}
                </div>
              </button>
            ))}
          </div>

          {/* Dev: quick credit grant */}
          {isDev && (
            <div className="mb-6 p-4 bg-app-panel-muted border border-dashed border-app-border-strong rounded-lg">
              <p className="text-xs uppercase tracking-wide text-app-text-subtle mb-2 font-medium">Dev mode</p>
              <Button variant="secondary" size="sm" className="w-full" onClick={handleDevGrant}>
                Add 1,000 test credits
              </Button>
            </div>
          )}

          {/* Payment info */}
          <div className="border-t border-app-border pt-6">
            <div className="flex items-center gap-2 text-sm text-app-text-muted">
              <CreditCard className="w-4 h-4" />
              <span>Secure payment powered by Stripe</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BillingPage;
